/**
 * What the app may honestly say about a swap's ORIGIN transaction.
 *
 * TWO AXES, NEVER CONFLATED. `origin_status` is chain evidence: did the
 * transaction this swap committed to actually land, and is it ours? The separate
 * provider status is settlement: did Relay or Across fill the other side? A
 * provider that has said nothing yet does not mean the origin failed, and an
 * origin that verified does not mean the swap is complete.
 *
 * Every mapping here is deliberately weaker than it could be. The failure that
 * motivated the whole feature was a swap that succeeded while the app knew
 * nothing, so the expensive mistake is claiming MORE than the evidence supports,
 * not less.
 */

import type { MessageKey } from '@/i18n/en';

/** The origin-axis states the server can report. */
export type OriginStatus =
  | 'unreported'
  | 'reported'
  | 'verified'
  | 'reverted'
  | 'unknown';

/** How the UI should treat one origin state. */
export interface OriginPresentation {
  /**
   * i18n key for the line shown to the user.
   *
   * Typed as MessageKey, not string. A missing key degrades to the raw key at
   * RUNTIME rather than throwing, so an untyped one would ship as the literal
   * text "swap.origin.reverted" on screen with nothing failing. This makes it a
   * compile error instead. Type-only import, so nothing is bundled.
   */
  messageKey: MessageKey;
  /**
   * Whether this state is settled on the origin axis. NOT whether the swap is
   * done -- completion is the provider axis's answer, always.
   */
  settled: boolean;
  /**
   * Whether the app may offer "try again".
   *
   * FALSE FOR 'unreported' AND 'unknown', and that is the single most important
   * rule in this file. Both mean a broadcast MAY have happened that we cannot
   * see. Offering a retry there invites a second transaction at a fresh nonce
   * beside a live signed payload, which is the actual double-spend shape. (A
   * same-nonce replacement is not, but the app does not build one.)
   */
  mayRetry: boolean;
  /** Whether this state deserves the user's attention rather than a spinner. */
  needsAttention: boolean;
}

const PRESENTATION: Record<OriginStatus, OriginPresentation> = {
  // "Submitting" -- NOT "not sent". The row exists because we were about to
  // sign; whether anything reached the chain is exactly what we do not know.
  unreported: {
    messageKey: 'swap.origin.unreported',
    settled: false,
    mayRetry: false,
    needsAttention: false,
  },
  // A hash was reported and is being checked. NOT "sent successfully": the hash
  // is an untrusted claim until the server matches it against the commitments
  // made before signing.
  reported: {
    messageKey: 'swap.origin.reported',
    settled: false,
    mayRetry: false,
    needsAttention: false,
  },
  // Confirmed on the origin chain. NOT "swap complete" -- the destination leg is
  // the provider axis and has its own answer.
  verified: {
    messageKey: 'swap.origin.verified',
    settled: true,
    mayRetry: false,
    needsAttention: false,
  },
  // It ran on chain and failed. A real, final outcome: no funds moved, and a
  // fresh attempt is safe because this nonce is spent.
  reverted: {
    messageKey: 'swap.origin.reverted',
    settled: true,
    mayRetry: true,
    needsAttention: true,
  },
  // WE COULD NOT SEE IT. Not "failed", not "did not happen". The user is told to
  // check their own wallet history, because that is the only reliable answer
  // available and it is not one this app can fetch for them.
  unknown: {
    messageKey: 'swap.origin.unknown',
    settled: false,
    mayRetry: false,
    needsAttention: true,
  },
};

/**
 * Resolve how to present an origin status.
 *
 * An UNRECOGNISED value maps to 'unknown', not to a default that reads as
 * progress. A server that grows a state this build has never heard of must
 * degrade to "we cannot confirm this", which is true, rather than to a
 * reassuring spinner, which would not be.
 *
 * @param status The server's origin_status, possibly absent or unfamiliar.
 * @returns The presentation rules for it.
 */
export function presentOrigin(status: string | undefined | null): OriginPresentation {
  const key = (status ?? '').trim().toLowerCase();
  if (key in PRESENTATION) return PRESENTATION[key as OriginStatus];
  return PRESENTATION.unknown;
}

/**
 * Whether a retry may be offered for a swap in this origin state.
 *
 * A thin wrapper over {@link presentOrigin} so call sites read as the rule they
 * are enforcing, and so the rule has one definition rather than a condition
 * copied into each button.
 *
 * @param status The server's origin_status.
 * @returns true only when a fresh attempt cannot double-spend.
 */
export function mayRetrySwap(status: string | undefined | null): boolean {
  return presentOrigin(status).mayRetry;
}

/**
 * Whether an AMBIGUOUS swap in this origin state must still block a new
 * attempt from the same form.
 *
 * A different question from {@link mayRetrySwap}. mayRetrySwap asks "may THIS
 * swap be retried"; this asks "may a NEW swap be started while that one is
 * unresolved". They diverge on 'reported' and 'verified': those mean the
 * broadcast demonstrably happened, so the ambiguity is RESOLVED and new swaps
 * are fine — while retrying that specific swap still is not.
 *
 * TRUE only for the two states where a broadcast may be outstanding unseen:
 * 'unreported', 'unknown' — and any unrecognised value, which presents as
 * unknown and must gate like it.
 *
 * @param status The server's origin_status, possibly absent or unfamiliar.
 * @returns true while a new attempt could double-spend.
 */
export function blocksNewAttempt(status: string | undefined | null): boolean {
  const key = (status ?? '').trim().toLowerCase();
  if (key === 'unreported' || key === 'unknown') return true;
  return !(key in PRESENTATION); // unfamiliar presents as unknown; gate like it
}

/**
 * Whether the origin axis is still moving and worth polling.
 *
 * 'unknown' counts as settled for POLLING purposes -- the server has given up
 * looking, so the client re-asking changes nothing -- while still being a state
 * that needs the user's attention. The two are different questions and this
 * returns only the first.
 *
 * @param status The server's origin_status.
 * @returns true while the server may still change its answer.
 */
export function originIsPending(status: string | undefined | null): boolean {
  const p = presentOrigin(status);
  return !p.settled && p.messageKey !== PRESENTATION.unknown.messageKey;
}
